import axiosInstance from './axios/axiosConfig'
import { endpoints } from './axios/endpoints'

/**
 * The holidays API (CommunicationManagementService routers/holidays.py).
 *
 * Every call answers `{ status, message, ... }`; lists carry `holidays`.
 */
const HolidayService = {
    /**
     * Holidays with `status` ("active", or "inactive", which the API also
     * fills with "completed" ones).
     */
    list: (status) =>
        axiosInstance.get(endpoints.holidays, {
            params: { event_status: status },
            // A read: the API's "Holidays Found" is not news worth a toast.
            silentToast: true,
        }),

    /** Holidays with `status` whose name contains `query` (case-sensitive, server side). */
    search: (status, query) =>
        axiosInstance.get(endpoints.holidaysSearch, {
            params: { event_status: status, search_param: query },
            silentToast: true,
        }),

    /**
     * Store one holiday. The API overwrites a holiday with the same id, so
     * the create page checks the id first.
     */
    create: (holiday) => axiosInstance.post(endpoints.createHoliday, holiday),

    /** Store many; the API refuses the list if an id is already an active holiday. */
    bulkCreate: (holidays) =>
        axiosInstance.post(endpoints.bulkHolidays, { holidays_list: holidays }),

    /** Replace a holiday's fields (all of them: send the whole holiday). */
    update: (holidayID, holiday) =>
        axiosInstance.put(endpoints.editHoliday(holidayID), holiday),

    /**
     * The id of every stored holiday, active, inactive or completed (the
     * inactive list carries the completed ones). POST /comms/holiday and the
     * bulk upload overwrite a holiday with the same id.
     */
    holidayIds: async () => {
        const [active, inactive] = await Promise.all([
            HolidayService.list('active'),
            HolidayService.list('inactive'),
        ])
        return [
            ...(active?.data?.holidays ?? []),
            ...(inactive?.data?.holidays ?? []),
        ].map((h) => h.holiday_id)
    },

    /**
     * Every id a new holiday must not reuse: all holidays (active, inactive,
     * completed) and every pipeline event type. POST /comms/holiday
     * overwrites a holiday with the same id, and the holiday's pipeline is
     * found by event type.
     */
    takenIds: async () => {
        const [ids, eventTypes] = await Promise.all([
            HolidayService.holidayIds(),
            axiosInstance.get(endpoints.getEventTypes),
        ])
        const types = Array.isArray(eventTypes?.data) ? eventTypes.data : []
        return [...ids, ...types]
    },

    /** Set only the status, leaving every other field as stored. */
    setStatus: (holidayID, status) =>
        axiosInstance.put(endpoints.holidayStatus(holidayID, status)),
}

export default HolidayService
